// ============================================================
// VCS.ChatVideo — Videonachrichten
// - Aufnahme (MediaRecorder), Upload, Abspielen
// - Karaoke-Untertitel: Wort-fuer-Wort-Hervorhebung bei Wiedergabe
// - Unterstuetzt Original- und uebersetzte Untertitel
// - Inline-Player in der Chat-Bubble + Fullscreen
// ============================================================
window.VCS = window.VCS || {};

VCS.ChatVideo = class {

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._view = {};
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    async startRecording() {
        const _t = (key, fb) => { const v = TRANSLATIONS[VCS.state.currentLang]?.[key] || TRANSLATIONS.de[key]; return v || fb; };
        const audioConstraints = {echoCancellation: true, noiseSuppression: true, autoGainControl: true};

        // Breite und Hoehe gelten auf dem iPhone in der Lage des Kamerasensors
        // (quer). 1280x720 liefert im Hochformat ein 720x1280-Bild mit vollem
        // Ausschnitt. Ein hochkant angefordertes 720x1280 schneidet Safari zu
        // einem flachen Streifen zu, das wirkt wie starker Zoom.
        const camSize = {width: {ideal: 1280}, height: {ideal: 720}};
        const camSizeSwapped = {width: {ideal: 720}, height: {ideal: 1280}};
        const vidConstraints = {facingMode: 'user', ...camSize};
        // Kamera und Mikrofon in einem Aufruf holen. Ein zweites getUserMedia
        // fuers Mikrofon bei laufender Kamera laesst Safari auf dem iPhone das
        // Kameraformat wechseln, die Vorschau springt dann sichtbar (Zoom).
        // Ohne Mikrofon-Freigabe geht es nur mit Kamera weiter; das Mikrofon
        // wird dann beim Aufnahme-Start nachgefragt.
        const openAttempts = [
            {video: vidConstraints, audio: audioConstraints},
            {video: vidConstraints, audio: false},
            {video: {facingMode: 'user'}, audio: false},
            {video: true, audio: false}
        ];
        let camStream = null;
        for (const constraints of openAttempts) {
            try {
                camStream = await navigator.mediaDevices.getUserMedia(constraints);
                break;
            } catch (_) {
                // naechste Variante probieren
            }
        }
        if (!camStream) {
            VCS.ui?.showToast?.(t('mediaError'), 'error');
            return;
        }

        const overlay = document.createElement('div');
        overlay.className = 'chat-photo-capture-overlay';
        overlay.innerHTML = `
        <div class="chat-photo-capture-container">
            <div class="chat-video-capture-video-wrap">
                <video class="chat-video-capture-video" autoplay playsinline muted></video>
                <div class="chat-photo-capture-rec-indicator hidden">
                    <span class="chat-recording-dot"></span>
                    <span class="chat-photo-capture-rec-time">0:00</span>
                </div>
                <div class="chat-photo-capture-hint">${escapeHtml(_t('chatVideoHintReady', 'Tippe auf den roten Button um die Aufnahme zu starten'))}</div>
            </div>
            <div class="chat-video-capture-preview hidden">
                <video class="chat-video-capture-preview-video" playsinline controls></video>
            </div>
            <div class="chat-photo-capture-progress hidden">
                <div class="chat-photo-capture-spinner"></div>
                <div class="chat-photo-capture-progress-text">${escapeHtml(_t('chatVideoSending', 'Video wird gesendet…'))}</div>
            </div>
            <label class="chat-video-capture-translate hidden">
                <input type="checkbox" class="chat-video-capture-translate-toggle" checked>
                <span>${escapeHtml(_t('chatVideoTranslateToggle', 'Video übersetzen'))}</span>
            </label>
            <div class="chat-photo-capture-actions">
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--cancel">${escapeHtml(_t('cancel', 'Abbrechen'))}</button>
                <button type="button" class="chat-photo-capture-btn chat-video-capture-btn--record">
                    <svg viewBox="0 0 24 24" width="48" height="48" fill="none">
                        <circle cx="12" cy="12" r="11" stroke="#ef4444" stroke-width="2"/>
                        <circle cx="12" cy="12" r="8" fill="#ef4444"/>
                    </svg>
                </button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--flip">
                    <svg class="chat-flip-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path class="chat-flip-circle" d="M21 12a9 9 0 1 1-3.6-7.2" />
                        <path d="M21 3v6h-6" />
                    </svg>
                </button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--stop hidden">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                        <rect x="4" y="4" width="16" height="16" rx="2"/>
                    </svg>
                    ${escapeHtml(_t('stop', 'Stopp'))}
                </button>
                <button type="button" class="chat-photo-capture-btn chat-photo-capture-btn--send hidden">${escapeHtml(_t('tooltipSend', 'Senden'))}</button>
            </div>
        </div>`;
        document.body.appendChild(overlay);

        let camVideo = overlay.querySelector('.chat-video-capture-video');
        const recIndicator = overlay.querySelector('.chat-photo-capture-rec-indicator');
        const recTimeEl = overlay.querySelector('.chat-photo-capture-rec-time');
        const recordBtn = overlay.querySelector('.chat-video-capture-btn--record');
        const stopBtn = overlay.querySelector('.chat-photo-capture-btn--stop');
        const sendBtn = overlay.querySelector('.chat-photo-capture-btn--send');
        const cancelBtn = overlay.querySelector('.chat-photo-capture-btn--cancel');
        const flipBtn = overlay.querySelector('.chat-photo-capture-btn--flip');
        const hintEl = overlay.querySelector('.chat-photo-capture-hint');
        const progressEl = overlay.querySelector('.chat-photo-capture-progress');
        const previewDiv = overlay.querySelector('.chat-video-capture-preview');
        const previewVideo = overlay.querySelector('.chat-video-capture-preview-video');
        const translateRow = overlay.querySelector('.chat-video-capture-translate');
        const translateToggle = overlay.querySelector('.chat-video-capture-translate-toggle');
        // Checkbox nur im 1:1-Chat bei anderer oder unbekannter Sprache
        const translateMode = VCS.chatMedia?.videoTranslateMode?.(VCS.state.chatActiveChatId) || 'group';
        translateRow.classList.toggle('hidden', translateMode !== 'choose');

        let currentFacing = 'user';
        let recorder = null;
        let recStream = null;
        let recTimerInterval = null;
        let recStartTime = 0;
        let videoBlob = null;
        let uploadMime = 'video/webm';
        let recStatusInterval = null;
        let dialogClosed = false;

        camVideo.srcObject = camStream;
        camVideo.play().catch(() => {});

        // Neuer Stream nach Kamerawechsel: frisches <video> einsetzen. Bekommt
        // dasselbe Element einen neuen Stream, passt Safari auf dem iPhone das
        // gedrehte Kamerabild als Querformat ein und die Vorschau wird klein.
        const replacePreviewVideo = (stream) => {
            const freshVideo = camVideo.cloneNode(false);
            camVideo.srcObject = null;
            camVideo.replaceWith(freshVideo);
            camVideo = freshVideo;
            camVideo.srcObject = stream;
            camVideo.play().catch(() => {});
        };

        // Wartet, bis die Vorschau eine Bildgroesse meldet (hoechstens 3 s)
        const waitForVideoSize = () => new Promise((resolve) => {
            if (camVideo.videoWidth > 0) { resolve(); return; }
            let timer = null;
            const done = () => {
                clearTimeout(timer);
                camVideo.removeEventListener('loadedmetadata', done);
                resolve();
            };
            timer = setTimeout(done, 3000);
            camVideo.addEventListener('loadedmetadata', done);
        });

        // Liefert die Kamera bei hochgehaltenem Handy trotzdem ein Querformat-Bild
        // (je nach Browser zaehlen Breite und Hoehe anders), die Kamera stoppen
        // und einmal mit vertauschten Werten neu anfordern. Sonst behaelt der
        // Zuschnitt auf 9:16 nur ein Drittel der Breite.
        const ensurePortraitCamera = async (step) => {
            await waitForVideoSize();
            if (dialogClosed) return;
            const isLandscapeFrame = camVideo.videoWidth > camVideo.videoHeight;
            const isPortraitScreen = window.innerHeight > window.innerWidth;
            if (!isLandscapeFrame || !isPortraitScreen) return;
            const facing = (camStream.getVideoTracks()[0]?.getSettings?.() || {}).facingMode || currentFacing;
            const audioTracks = camStream.getAudioTracks();
            camStream.getVideoTracks().forEach(tr => tr.stop());
            let videoStream;
            try {
                videoStream = await navigator.mediaDevices.getUserMedia({
                    video: {facingMode: facing, ...camSizeSwapped},
                    audio: false
                });
            } catch (err) {
                VCS.logRemote?.('error', '[video-cam] ' + step + ' restart FAIL name=' + (err?.name || '?') + ' message=' + (err?.message || err));
                VCS.ui?.showToast?.(t('mediaError'), 'error');
                return;
            }
            if (dialogClosed) {
                videoStream.getTracks().forEach(tr => tr.stop());
                return;
            }
            camStream = new MediaStream([...videoStream.getVideoTracks(), ...audioTracks]);
            replacePreviewVideo(camStream);
            await waitForVideoSize();
        };

        let camReady = ensurePortraitCamera('open');

        const cleanup = () => {
            dialogClosed = true;
            if (recStatusInterval) { clearInterval(recStatusInterval); recStatusInterval = null; }
            camStream.getTracks().forEach(tr => tr.stop());
            if (recStream) {
                recStream._stopCanvas?.();
                recStream.getTracks().forEach(tr => tr.stop());
            }
            if (recTimerInterval) clearInterval(recTimerInterval);
            overlay.remove();
        };

        cancelBtn.addEventListener('click', cleanup);

        flipBtn.addEventListener('click', async () => {
            const newFacing = currentFacing === 'user' ? 'environment' : 'user';
            flipBtn.classList.remove('spinning');
            void flipBtn.offsetWidth;
            flipBtn.classList.add('spinning');
            setTimeout(() => flipBtn.classList.remove('spinning'), 550);
            await camReady;
            // Nur die Kamera tauschen, das Mikrofon bleibt offen
            const audioTracks = camStream.getAudioTracks();
            camStream.getVideoTracks().forEach(tr => tr.stop());
            let videoStream;
            try {
                videoStream = await navigator.mediaDevices.getUserMedia({
                    video: {facingMode: {exact: newFacing}, ...camSize},
                    audio: false
                });
            } catch (_) {
                try {
                    videoStream = await navigator.mediaDevices.getUserMedia({
                        video: {facingMode: newFacing, ...camSize},
                        audio: false
                    });
                } catch (_2) {
                    videoStream = await navigator.mediaDevices.getUserMedia({
                        video: {facingMode: currentFacing, ...camSize},
                        audio: false
                    });
                }
            }
            camStream = new MediaStream([...videoStream.getVideoTracks(), ...audioTracks]);
            replacePreviewVideo(camStream);
            currentFacing = newFacing;
            camReady = ensurePortraitCamera('flip');
        });

        recordBtn.addEventListener('click', async () => {
            await camReady;
            // Das Mikrofon kommt normalerweise schon aus dem Kamera-Stream und
            // wird nur nachgefragt, wenn es beim Oeffnen nicht freigegeben wurde
            let audioTracks = camStream.getAudioTracks().filter(tr => tr.readyState === 'live');
            if (!audioTracks.length) {
                try {
                    const audioStream = await navigator.mediaDevices.getUserMedia({audio: audioConstraints});
                    audioTracks = audioStream.getAudioTracks();
                } catch (_) {
                    VCS.ui?.showToast?.(t('micCheckFailed') || 'Kein Mikrofon-Zugriff', 'error');
                    return;
                }
            }

            const vw = camVideo.videoWidth || 640;
            const vh = camVideo.videoHeight || 480;
            // 540x960 (9:16). Bewusst unter 720p: Safari auf dem iPhone kommt
            // beim Aufnehmen ueber Canvas sonst ins Stocken. 270x480 war zu
            // klein und wurde auf dem Display hochskaliert.
            const targetW = 540;
            const targetH = 960;
            const targetRatio = targetW / targetH;
            const srcRatio = vw / vh;
            let sx, sy, sw, sh;
            if (srcRatio > targetRatio) {
                sh = vh;
                sw = Math.round(vh * targetRatio);
                sx = Math.round((vw - sw) / 2);
                sy = 0;
            } else {
                sw = vw;
                sh = Math.round(vw / targetRatio);
                sx = 0;
                sy = Math.round((vh - sh) / 2);
            }
            const recCanvas = document.createElement('canvas');
            recCanvas.width = targetW;
            recCanvas.height = targetH;
            const recCtx = recCanvas.getContext('2d');
            let canvasAnimId = null;
            const drawFrame = () => {
                recCtx.drawImage(camVideo, sx, sy, sw, sh, 0, 0, targetW, targetH);
                canvasAnimId = requestAnimationFrame(drawFrame);
            };
            drawFrame();
            const canvasStream = recCanvas.captureStream(24);

            recStream = new MediaStream([
                ...canvasStream.getVideoTracks(),
                ...audioTracks
            ]);
            recStream._stopCanvas = () => { if (canvasAnimId) cancelAnimationFrame(canvasAnimId); };

            const mimeType = MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4'
                : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus'
                : MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : '';
            const recorderOptions = {videoBitsPerSecond: 2500000, audioBitsPerSecond: 96000};
            if (mimeType) recorderOptions.mimeType = mimeType;
            uploadMime = mimeType.includes('mp4') ? 'video/mp4' : 'video/webm';

            recorder = new MediaRecorder(recStream, recorderOptions);
            const chunks = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = () => {
                videoBlob = new Blob(chunks, {type: uploadMime});
                // Tracks erst nach Blob-Erstellung stoppen
                if (recStream) {
                    recStream._stopCanvas?.();
                    recStream.getTracks().forEach(tr => tr.stop());
                }
                camStream.getTracks().forEach(tr => tr.stop());
                // Preview starten
                if (videoBlob) {
                    const url = URL.createObjectURL(videoBlob);
                    previewVideo.src = url;
                }
            };
            // Timeslice ist Pflicht: Ohne regelmaessige Teilausgabe verliert Safari
            // auf dem iPhone ab einer wechselnden Stelle das Bild, der Ton laeuft weiter
            recorder.start(1000);

            if (VCS.state.chatActiveChatId) {
                const recPayload = { type: 'chat-recording', chatId: VCS.state.chatActiveChatId, phone: VCS.state.chatMyPhone, mode: 'video' };
                VCS.chat?.wsSend?.(recPayload);
                recStatusInterval = setInterval(() => {
                    if (VCS.state.chatActiveChatId) VCS.chat?.wsSend?.(recPayload);
                }, 3000);
            }

            recStartTime = Date.now();
            recIndicator.classList.remove('hidden');
            recTimerInterval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - recStartTime) / 1000);
                const m = Math.floor(elapsed / 60);
                const s = elapsed % 60;
                recTimeEl.textContent = m + ':' + String(s).padStart(2, '0');
            }, 500);

            recordBtn.classList.add('hidden');
            flipBtn.classList.add('hidden');
            stopBtn.classList.remove('hidden');
            hintEl.textContent = _t('chatVideoHintRecording', 'Aufnahme läuft… Tippe auf Stopp wenn du fertig bist');
        });

        stopBtn.addEventListener('click', () => {
            if (recStatusInterval) { clearInterval(recStatusInterval); recStatusInterval = null; }
            if (recorder && recorder.state !== 'inactive') recorder.stop();
            if (recTimerInterval) { clearInterval(recTimerInterval); recTimerInterval = null; }
            recIndicator.classList.add('hidden');

            overlay.querySelector('.chat-video-capture-video-wrap').classList.add('hidden');
            previewDiv.classList.remove('hidden');

            stopBtn.classList.add('hidden');
            sendBtn.classList.remove('hidden');
            hintEl.classList.add('chat-photo-capture-hint--below');
            hintEl.textContent = _t('chatVideoHintSend', 'Tippe auf Senden um das Video abzuschicken');
        });

        sendBtn.addEventListener('click', async () => {
            if (!videoBlob || videoBlob.size === 0) return;
            const durationMs = Date.now() - recStartTime;
            if (durationMs < 500) return;

            // 'same': gleiche Sprache, keine Verarbeitung. 'choose': die Checkbox entscheidet.
            const skipVideoTranslation = translateMode === 'same'
                || (translateMode === 'choose' && !translateToggle.checked);

            overlay.querySelector('.chat-photo-capture-actions').classList.add('hidden');
            translateRow.classList.add('hidden');
            previewDiv.classList.add('hidden');
            hintEl.classList.add('hidden');
            progressEl.classList.remove('hidden');

            try {
                if (previewVideo.src) { previewVideo.pause(); URL.revokeObjectURL(previewVideo.src); }
                cleanup();
                await this.uploadRecordedVideo(videoBlob, uploadMime, {skipVideoTranslation});
            } catch (err) {
                console.error('[chat] video capture upload error:', err);
                VCS.ui?.showToast?.(t('mediaError'), 'error');
            }
        });
    }

    async uploadRecordedVideo(blob, mimeType, opts) {
        const normalizedMime = mimeType.includes('mp4') ? 'video/mp4' : 'video/webm';
        const ext = normalizedMime.includes('mp4') ? '.mp4' : '.webm';
        const file = new File([blob], 'video-message' + ext, {type: normalizedMime});
        const limit = VCS.chatMedia?.maxUploadSize?.('video') ?? VCS.ChatMedia.MAX_SIZE;
        if (file.size > limit) {
            const key = VCS.chatMedia?.tooLargeKey?.('video') || 'mediaFileTooLarge';
            VCS.ui?.showToast?.(t(key), 'error');
            return;
        }
        await VCS.chatMedia?.uploadMedia?.(file, '', opts);
    }

    async playVideo(posterEl, src, messageId, autoplay = true, initialDurationMs = null, posterUrl = null, revokeBlobOnDetach = true) {
        const playSrc = src;
        const isBlob = typeof playSrc === 'string' && playSrc.startsWith('blob:');

        const video = document.createElement('video');
        video.src = playSrc;
        video.controls = false;
        video.playsInline = true;
        // autoplay=false: gar nichts vorab laden — Browser holt das Video erst beim Klick.
        // Vermeidet Network-Storm beim Chat-Eintritt (Android-Chrome warf code=2 bei
        // parallelen preload="metadata"-Requests). Trade-off: Bar zeigt 0:00 / 0:00
        // bis zum ersten Play, dann uebernimmt der reale Wert.
        video.preload = autoplay ? 'auto' : 'none';
        // Erstes Frame als Poster (Server-generiert beim Upload, gecached vom Browser
        // ueber Cache-Control: max-age=86400). Browser zeigt das Bild bis zum ersten
        // Play-Frame.
        if (posterUrl) video.poster = posterUrl;
        video.className = 'chat-vp-video';
        if (messageId) video.dataset.msgId = messageId;

        if (VCS.state.directCallActive || VCS.state.groupCallActive) {
            video.muted = true;
        }

        const container = this._buildPlayerContainer(video, messageId || null);
        posterEl.replaceWith(container);

        if (messageId) {
            const captionEl = container.querySelector('.chat-video-caption');
            const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
            const msgObj = msgs.find(m => m.id === messageId);
            const isMine = msgObj?.senderPhone === VCS.state.chatMyPhone;
            const forceOriginal = isMine ? msgObj?._preferTranslatedVideo !== true : msgObj?._preferTranslatedVideo === false;
            const karaokeUpdate = this.videoKaraoke(video, messageId, captionEl, forceOriginal);
            if (karaokeUpdate) {
                video.ontimeupdate = karaokeUpdate;
            }
        }

        if (isBlob && revokeBlobOnDetach) {
            this._registerBlobForCleanup(container, playSrc);
        }

        this._wireControls(container, video, messageId || null, isBlob);

        // Vorab-Duration aus DB (vom Upload via ffprobe) — Bar zeigt korrekte Zeit
        // sofort beim Mount, ohne dass der Browser Metadaten laden muss.
        // loadedmetadata-Handler ueberschreibt spaeter mit dem realen Browser-Wert.
        if (initialDurationMs && initialDurationMs > 0) {
            const durSec = initialDurationMs / 1000;
            const timeEl = container.querySelector('[data-vp="time"]');
            const scrubber = container.querySelector('.chat-vp-scrubber');
            if (timeEl) {
                const m = Math.floor(durSec / 60);
                const s = Math.floor(durSec % 60);
                timeEl.textContent = '0:00 / ' + m + ':' + String(s).padStart(2, '0');
            }
            if (scrubber) {
                scrubber.setAttribute('aria-valuemax', String(Math.floor(durSec)));
            }
        }

        if (autoplay) {
            video.play().catch(() => {});
        }
        return container;
    }

    // Mountet alle .chat-video-poster im uebergebenen Container als Player ohne Autoplay,
    // sodass die Control-Bar sofort sichtbar ist. Wird nach Bubble-Render aufgerufen.
    mountVideoPostersIn(rootEl) {
        if (!rootEl) return;
        const posters = rootEl.querySelectorAll('.chat-video-poster[data-src]');
        posters.forEach(poster => {
            const src = poster.getAttribute('data-src');
            if (!src || src === 'pending') return;
            const msgId = poster.getAttribute('data-karaoke') || null;
            const durationMs = parseInt(poster.getAttribute('data-duration-ms') || '0', 10) || null;
            const posterUrl = poster.getAttribute('data-poster') || null;
            // Marker, damit ein zweiter Render-Lauf nicht doppelt mountet
            if (poster.dataset.vpMounted === '1') return;
            poster.dataset.vpMounted = '1';
            this._mountVideoPoster(poster, src, msgId, durationMs, posterUrl)
                .catch(e => console.error('[chat] video mount error:', e));
        });
    }

    // Server-URL: erst im lokalen Cache nachsehen (nicht auf Apple-Geraeten).
    // Nicht gespeichert -> vom Server streamen und beim ersten Play im Hintergrund speichern.
    // revokeBlobOnDetach=false: Blob-URLs gehoeren mediaCache bzw. der Message, nie dem Player.
    async _mountVideoPoster(poster, src, msgId, durationMs, posterUrl) {
        const cache = VCS.mediaCache;
        const canCache = !src.startsWith('blob:') && !!cache?.canCacheVideo?.();
        let playSrc = src;
        if (canCache) {
            playSrc = (await cache.getCached(src).catch(() => null)) || src;
        }
        if (!poster.isConnected) return;

        const container = await this.playVideo(poster, playSrc, msgId, false, durationMs, posterUrl, false);
        // Schluessel fuer renderChatMessages: gleicher Poster -> Player wiederverwenden.
        if (container) container.dataset.vpKey = src + '|' + (msgId || '');
        if (canCache && playSrc === src) {
            container?.querySelector('video')?.addEventListener('play', () => {
                cache.storeVideo(src).catch(() => {});
            }, { once: true });
        }
    }

    // Beendet das Pseudo-Vollbild (CSS-Fallback fuer Browser ohne
    // Element-Vollbild). Wird von der Zurueck-Navigation gerufen; echtes
    // Element-Vollbild beendet der Browser selbst.
    exitFullscreenFallback(containerEl) {
        const el = containerEl || document.querySelector('.chat-video-player-wrap.chat-vp--fs-fallback');
        el?._vpExitFallbackFs?.();
    }

    // ── Custom Video Player ──────────────────────────────────
    // Wrapper around <video>; native controls disabled. Karaoke contract
    // (data-msg-id on <video>, .chat-video-caption sibling, ontimeupdate)
    // is preserved for videoKaraoke().

    _t(key, fb) {
        // Use the global i18n helper; fall back to provided string if the
        // key is missing in current locale (and missing in de.json).
        const v = t(key);
        return (v && v !== key) ? v : fb;
    }

    _vpLog(level, msg) {
        try {
            VCS.signaling?.send?.({
                type: 'client-log',
                level: level || 'info',
                msg: '[ChatVideoPlayer] ' + msg,
                ua : (navigator.userAgent || '').slice(0, 60)
            });
        } catch (_) { /* noop */ }
    }

    _registerBlobForCleanup(container, blobUrl) {
        // WeakMap holds container -> blobUrl. MutationObserver below revokes
        // the URL once the container leaves the document. WeakMap so that
        // GC can reap entries even if observer fails.
        if (!this._blobUrls) this._blobUrls = new WeakMap();
        this._blobUrls.set(container, blobUrl);

        if (!this._detachObserver) {
            this._detachObserver = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    m.removedNodes.forEach((node) => {
                        // Direct removal
                        this._maybeRevokeBlob(node);
                        // Or removal of an ancestor that contains a tracked container
                        if (node.querySelectorAll) {
                            node.querySelectorAll('.chat-video-player-wrap').forEach((c) => {
                                this._maybeRevokeBlob(c);
                            });
                        }
                    });
                }
            });
            this._detachObserver.observe(document.body, { childList: true, subtree: true });
        }
    }

    _maybeRevokeBlob(node) {
        if (!node || !this._blobUrls) return;
        const url = this._blobUrls.get(node);
        if (!url) return;
        try {
            URL.revokeObjectURL(url);
            this._vpLog('info', 'blob revoked on detach');
        } catch (_) { /* noop */ }
        this._blobUrls.delete(node);
    }

    _buildPlayerContainer(video, messageId) {
        const t = this._t.bind(this);
        const container = document.createElement('div');
        container.className = 'chat-video-player-wrap';
        if (messageId) container.dataset.msgId = messageId;

        // <video> is the FIRST child (karaoke selector findet es ueber data-msg-id,
        // .chat-video-caption als Sibling).
        container.appendChild(video);

        // Karaoke caption layer.
        const captionEl = document.createElement('div');
        captionEl.className = 'chat-video-caption';
        container.appendChild(captionEl);

        // Center overlay (Play / Spinner / Error). Single element, content swaps.
        const overlay = document.createElement('div');
        overlay.className = 'chat-vp-overlay';
        overlay.innerHTML = `
            <button type="button" class="chat-vp-overlay-play" aria-label="${escapeHtml(t('videoPlayAria', 'Abspielen'))}">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><polygon points="6,3 20,12 6,21"></polygon></svg>
            </button>`;
        container.appendChild(overlay);

        // Control bar (Play, Scrubber, Time, CC, Mute).
        const bar = document.createElement('div');
        bar.className = 'chat-vp-controls';
        bar.innerHTML = `
            <button type="button" class="chat-vp-btn chat-vp-btn--play" data-vp="playpause"
                    aria-label="${escapeHtml(t('videoPlayAria', 'Abspielen'))}">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" data-vp-icon="play"><polygon points="6,3 20,12 6,21"></polygon></svg>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" data-vp-icon="pause" style="display:none"><rect x="5" y="4" width="4" height="16"/><rect x="15" y="4" width="4" height="16"/></svg>
            </button>
            <div class="chat-vp-scrubber" role="slider" tabindex="0"
                 aria-label="${escapeHtml(t('videoScrubberAria', 'Wiedergabeposition'))}"
                 aria-valuemin="0" aria-valuemax="0" aria-valuenow="0">
                <div class="chat-vp-scrubber-track"></div>
                <div class="chat-vp-scrubber-fill"></div>
                <div class="chat-vp-scrubber-thumb"></div>
            </div>
            <span class="chat-vp-time" data-vp="time">0:00 / 0:00</span>
            <button type="button" class="chat-vp-btn chat-video-cc-btn" data-vp="cc"
                    aria-label="${escapeHtml(t('videoCaptionsOnAria', 'Untertitel einblenden'))}"
                    title="${escapeHtml(t('videoCaptionsToggle', 'Untertitel'))}">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                    <path d="M7 12h3M7 15h2"></path>
                    <path d="M14 12h3M14 15h2"></path>
                </svg>
            </button>
            <button type="button" class="chat-vp-btn chat-vp-btn--mute" data-vp="mute"
                    aria-label="${escapeHtml(t('videoMuteAria', 'Stumm schalten'))}">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" data-vp-icon="sound">
                    <path d="M3 10v4h4l5 4V6L7 10H3z"/>
                    <path d="M16.5 12a4.5 4.5 0 00-2-3.75v7.5A4.5 4.5 0 0016.5 12z"/>
                </svg>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" data-vp-icon="muted" style="display:none">
                    <path d="M3 10v4h4l5 4V6L7 10H3z"/>
                    <line x1="16" y1="9" x2="22" y2="15" stroke="currentColor" stroke-width="2"/>
                    <line x1="22" y1="9" x2="16" y2="15" stroke="currentColor" stroke-width="2"/>
                </svg>
            </button>`;
        container.appendChild(bar);

        // Vollbild-Button sitzt oben rechts im Bild, nicht in der Control-Bar:
        // die Bar ist bei 270px Bubble-Breite mit Play/Scrubber/Zeit/CC/Mute
        // bereits randvoll. Eigenes Kind des Containers — NICHT im Overlay,
        // dessen innerHTML von Spinner/Fehlerkarte ueberschrieben wird.
        const fsBtn = document.createElement('button');
        fsBtn.type = 'button';
        fsBtn.className = 'chat-vp-fs-btn';
        fsBtn.dataset.vp = 'fullscreen';
        fsBtn.setAttribute('aria-label', t('videoFullscreenAria', 'Vollbild'));
        fsBtn.setAttribute('title', t('videoFullscreenAria', 'Vollbild'));
        fsBtn.innerHTML = `
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round" data-vp-icon="expand">
                <path d="M9 4H6a2 2 0 00-2 2v3"/>
                <path d="M15 4h3a2 2 0 012 2v3"/>
                <path d="M9 20H6a2 2 0 01-2-2v-3"/>
                <path d="M15 20h3a2 2 0 002-2v-3"/>
            </svg>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round" data-vp-icon="compress"
                 style="display:none">
                <path d="M4 9h3a2 2 0 002-2V4"/>
                <path d="M20 9h-3a2 2 0 01-2-2V4"/>
                <path d="M4 15h3a2 2 0 012 2v3"/>
                <path d="M20 15h-3a2 2 0 00-2 2v3"/>
            </svg>`;
        container.appendChild(fsBtn);

        return container;
    }

    _wireControls(container, video, messageId, isBlob) {
        const t = this._t.bind(this);
        const overlay      = container.querySelector('.chat-vp-overlay');
        const overlayPlay  = container.querySelector('.chat-vp-overlay-play');
        const bar          = container.querySelector('.chat-vp-controls');

        // Initial: bar always visible (auto-hide kicks in only after the
        // 'play' event below — never on mount, never while paused/ended).
        container.classList.remove('chat-vp-controls--hidden');

        // Bubble-Swipe-Handler ignoriert Touches innerhalb von .chat-video-player-wrap
        // bereits selbst (chat-messages.js). Hier kein zusaetzliches stopPropagation
        // mit passive:true — das blockiert iOS-Safari nicht vor Page-Scroll und schluckt
        // nur den Event, ohne preventDefault rufen zu koennen.
        const playBtn      = bar.querySelector('[data-vp="playpause"]');
        const playIcon     = playBtn.querySelector('[data-vp-icon="play"]');
        const pauseIcon    = playBtn.querySelector('[data-vp-icon="pause"]');
        const muteBtn      = bar.querySelector('[data-vp="mute"]');
        const soundIcon    = muteBtn.querySelector('[data-vp-icon="sound"]');
        const mutedIcon    = muteBtn.querySelector('[data-vp-icon="muted"]');
        const ccBtn        = bar.querySelector('[data-vp="cc"]');
        const fsBtn        = container.querySelector('.chat-vp-fs-btn');
        const fsExpandIcon = fsBtn?.querySelector('[data-vp-icon="expand"]');
        const fsCompIcon   = fsBtn?.querySelector('[data-vp-icon="compress"]');
        const timeEl       = bar.querySelector('[data-vp="time"]');
        const scrubber     = bar.querySelector('.chat-vp-scrubber');
        const fill         = bar.querySelector('.chat-vp-scrubber-fill');
        const thumb        = bar.querySelector('.chat-vp-scrubber-thumb');

        const fmt = (sec) => {
            if (!isFinite(sec) || sec < 0) sec = 0;
            const m = Math.floor(sec / 60);
            const s = Math.floor(sec % 60);
            return m + ':' + String(s).padStart(2, '0');
        };

        // Safari mit preload="metadata" rejected den ersten play()-Aufruf, weil
        // noch keine Daten geladen sind. Fallback: load() + auf canplay warten.
        // Zusaetzlich: Replay nach Ende — currentTime auf 0 zuruecksetzen.
        const safePlay = () => {
            if (video.ended || (isFinite(video.duration) && video.duration > 0 && video.currentTime >= video.duration - 0.1)) {
                try { video.currentTime = 0; } catch (_) {}
            }
            const p = video.play();
            if (p && typeof p.catch === 'function') {
                p.catch(() => {
                    video.addEventListener('canplay', () => {
                        video.play().catch(() => {});
                    }, { once: true });
                    try { video.load(); } catch (_) {}
                });
            }
        };

        // ── Auto-Hide-Logic ──────────────────────────────────
        let hideTimer = null;
        const cancelHide = () => { if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; } };
        const scheduleHide = (delayMs) => {
            cancelHide();
            // Only auto-hide while playing; never in paused/error state.
            if (video.paused || video.ended) return;
            // Very short videos (<4s): keep bar visible (Lea-Spec 7.2).
            if (isFinite(video.duration) && video.duration > 0 && video.duration < 4) return;
            hideTimer = setTimeout(() => {
                container.classList.add('chat-vp-controls--hidden');
            }, delayMs);
        };
        const showBar = () => {
            container.classList.remove('chat-vp-controls--hidden');
            scheduleHide(2000);
        };

        // ── Spinner mit 250ms Flicker-Schutz ─────────────────
        let spinnerTimer = null;
        const showSpinner = () => {
            if (spinnerTimer) return;
            spinnerTimer = setTimeout(() => {
                if (overlay.dataset.vpState === 'error') return;
                overlay.dataset.vpState = 'spinner';
                overlay.innerHTML = '<div class="chat-vp-overlay-spinner" aria-hidden="true"></div>';
            }, 250);
        };
        const hideSpinner = () => {
            if (spinnerTimer) { clearTimeout(spinnerTimer); spinnerTimer = null; }
            if (overlay.dataset.vpState === 'spinner') {
                overlay.dataset.vpState = '';
                overlay.innerHTML = '';
            }
        };

        const showOverlayPlay = () => {
            if (overlay.dataset.vpState === 'error') return;
            if (spinnerTimer) { clearTimeout(spinnerTimer); spinnerTimer = null; }
            overlay.dataset.vpState = 'play';
            overlay.innerHTML = `
                <button type="button" class="chat-vp-overlay-play" aria-label="${escapeHtml(t('videoPlayAria', 'Abspielen'))}">
                    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor"><polygon points="6,3 20,12 6,21"></polygon></svg>
                </button>`;
            overlay.querySelector('.chat-vp-overlay-play').addEventListener('click', (e) => {
                e.stopPropagation();
                safePlay();
            });
        };
        const clearOverlay = () => {
            if (overlay.dataset.vpState === 'error') return;
            overlay.dataset.vpState = '';
            overlay.innerHTML = '';
            if (spinnerTimer) { clearTimeout(spinnerTimer); spinnerTimer = null; }
        };

        // ── Error-Card ───────────────────────────────────────
        const showErrorCard = (errType) => {
            // errType: 'decrypt' | 'stream' | 'missing'
            container.classList.remove('chat-vp-controls--hidden');
            bar.style.display = 'none';
            if (fsBtn) fsBtn.style.display = 'none';
            overlay.dataset.vpState = 'error';

            let titleKey, hintKey, ariaKey, ctaKey, ctaIsRetry;
            if (errType === 'decrypt') {
                titleKey = 'videoErrorDecryptTitle'; hintKey = 'videoErrorDecryptHint';
                ariaKey  = 'videoErrorDecryptAria';  ctaKey  = 'videoErrorDecryptClose'; ctaIsRetry = false;
            } else if (errType === 'missing') {
                titleKey = 'videoErrorMissingTitle'; hintKey = 'videoErrorMissingHint';
                ariaKey  = 'videoErrorMissingAria';  ctaKey  = 'videoErrorMissingClose'; ctaIsRetry = false;
            } else {
                titleKey = 'videoErrorStreamTitle';  hintKey = 'videoErrorStreamHint';
                ariaKey  = 'videoErrorStreamAria';   ctaKey  = 'videoErrorStreamRetry';  ctaIsRetry = true;
            }

            const title = escapeHtml(t(titleKey, 'Wiedergabe nicht möglich'));
            const hint  = escapeHtml(t(hintKey,  ''));
            const aria  = escapeHtml(t(ariaKey,  title));
            const cta   = escapeHtml(t(ctaKey,   ctaIsRetry ? 'Erneut versuchen' : 'Schließen'));
            const closeAria = escapeHtml(t('videoCloseErrorAria', 'Fehlermeldung schließen'));
            const retryAria = escapeHtml(t('videoErrorStreamRetryAria', 'Video erneut laden'));

            overlay.innerHTML = `
                <div class="chat-vp-error" role="alert" aria-live="polite" aria-label="${aria}" data-error-type="${errType}">
                    <button type="button" class="chat-vp-error-close" aria-label="${closeAria}">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>
                        </svg>
                    </button>
                    <div class="chat-vp-error-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                    </div>
                    <div class="chat-vp-error-title">${title}</div>
                    <div class="chat-vp-error-hint">${hint}</div>
                    ${ctaIsRetry ? `<button type="button" class="chat-vp-error-retry" aria-label="${retryAria}">${cta}</button>` : ''}
                </div>`;

            const closeBtn = overlay.querySelector('.chat-vp-error-close');
            if (closeBtn) closeBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                overlay.dataset.vpState = '';
                overlay.innerHTML = '';
                bar.style.display = '';
                if (fsBtn) fsBtn.style.display = '';
            });
            const retryBtn = overlay.querySelector('.chat-vp-error-retry');
            if (retryBtn) retryBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                overlay.dataset.vpState = '';
                overlay.innerHTML = '';
                bar.style.display = '';
                if (fsBtn) fsBtn.style.display = '';
                try { video.load(); } catch (_) {}
                video.play().catch(() => {});
            });
        };

        // ── Stalled-Timer (split: blob vs https) ─────────────
        let stalledTimer = null;
        const armStalledTimer = () => {
            if (stalledTimer) clearTimeout(stalledTimer);
            const ms = isBlob ? 3000 : 10000;
            stalledTimer = setTimeout(() => {
                if (video.paused || video.ended) return;
                this._vpLog('warn', 'stalled timeout fired (' + (isBlob ? 'blob/decrypt' : 'stream') + ', ' + ms + 'ms)');
                showErrorCard(isBlob ? 'decrypt' : 'stream');
            }, ms);
        };
        const clearStalledTimer = () => {
            if (stalledTimer) { clearTimeout(stalledTimer); stalledTimer = null; }
        };

        // ── Video event listeners ───────────────────────────
        video.addEventListener('loadedmetadata', () => {
            const dur = video.duration;
            if (isFinite(dur) && dur > 0) {
                scrubber.setAttribute('aria-valuemax', String(Math.floor(dur)));
                timeEl.textContent = '0:00 / ' + fmt(dur);
            }
        });

        // Nach Video-Ende: Bar wieder einblenden + grosses Play-Overlay zeigen.
        // safePlay() setzt beim naechsten Klick currentTime=0 und startet von vorne.
        // Caption leeren — :empty { display: none } im CSS blendet die Box aus.
        video.addEventListener('ended', () => {
            cancelHide();
            container.classList.remove('chat-vp-controls--hidden');
            showOverlayPlay();
            const captionEl = container.querySelector('.chat-video-caption');
            if (captionEl) captionEl.textContent = '';
        });

        video.addEventListener('timeupdate', () => {
            // Note: ontimeupdate is owned by videoKaraoke() — we use addEventListener
            // so both run. Karaoke contract requires we never overwrite ontimeupdate.
            const dur = video.duration;
            const cur = video.currentTime;
            if (isFinite(dur) && dur > 0) {
                const pct = Math.max(0, Math.min(1, cur / dur));
                fill.style.width   = (pct * 100) + '%';
                thumb.style.left   = (pct * 100) + '%';
                timeEl.textContent = fmt(cur) + ' / ' + fmt(dur);
                scrubber.setAttribute('aria-valuenow', String(Math.floor(cur)));
                scrubber.setAttribute('aria-valuetext',
                    (t('videoScrubberValueText', '{current} von {total}') || '{current} von {total}')
                        .replace('{current}', fmt(cur))
                        .replace('{total}',   fmt(dur))
                );
            }
        });

        video.addEventListener('play', () => {
            playIcon.style.display  = 'none';
            pauseIcon.style.display = '';
            playBtn.setAttribute('aria-label', t('videoPauseAria', 'Pause'));
            clearOverlay();
            showBar();
            armStalledTimer();
        });

        video.addEventListener('playing', () => {
            hideSpinner();
            // Video laeuft tatsaechlich — Stalled-Timer loeschen. Wird neu armed
            // bei 'waiting'/'stalled', falls das Streaming spaeter ins Stocken geraet.
            clearStalledTimer();
        });

        video.addEventListener('pause', () => {
            playIcon.style.display  = '';
            pauseIcon.style.display = 'none';
            playBtn.setAttribute('aria-label', t('videoPlayAria', 'Abspielen'));
            cancelHide();
            container.classList.remove('chat-vp-controls--hidden');
            if (!video.ended && overlay.dataset.vpState !== 'error') {
                showOverlayPlay();
            }
            clearStalledTimer();
        });

        video.addEventListener('waiting', () => {
            showSpinner();
            armStalledTimer();
        });

        video.addEventListener('canplay', () => { hideSpinner(); clearStalledTimer(); });
        video.addEventListener('canplaythrough', () => { hideSpinner(); clearStalledTimer(); });

        video.addEventListener('stalled', () => {
            this._vpLog('warn', 'stalled event');
            showSpinner();
            armStalledTimer();
        });

        video.addEventListener('ended', () => {
            cancelHide();
            clearStalledTimer();
        });

        video.addEventListener('error', () => {
            const err = video.error;
            const code = err ? err.code : 0;
            // MEDIA_ERR_NETWORK = 2, MEDIA_ERR_DECODE = 3, MEDIA_ERR_SRC_NOT_SUPPORTED = 4
            this._vpLog('error', 'video error code=' + code + ' isBlob=' + isBlob);
            clearStalledTimer();
            // For blob: sources, almost always a decrypt/decode issue.
            // For https: distinguish source-not-supported (often 404/410) from network.
            if (isBlob) {
                showErrorCard('decrypt');
            } else if (code === 4) {
                showErrorCard('missing');
            } else {
                showErrorCard('stream');
            }
        });

        // ── Bar interactions ────────────────────────────────
        playBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (video.paused) safePlay();
            else              video.pause();
        });

        muteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            video.muted = !video.muted;
            soundIcon.style.display = video.muted ? 'none' : '';
            mutedIcon.style.display = video.muted ? '' : 'none';
            muteBtn.setAttribute('aria-label',
                video.muted ? t('videoUnmuteAria', 'Ton einschalten') : t('videoMuteAria', 'Stumm schalten')
            );
        });
        // Reflect initial mute state in the icon (for callActive default-mute).
        if (video.muted) {
            soundIcon.style.display = 'none';
            mutedIcon.style.display = '';
            muteBtn.setAttribute('aria-label', t('videoUnmuteAria', 'Ton einschalten'));
        }

        // ── Vollbild ────────────────────────────────────────
        // Vollbild laeuft auf dem Wrapper, NICHT auf <video>: nur so bleiben die
        // eigene Control-Bar und die Karaoke-Untertitel sichtbar. iPhone-Safari
        // kennt kein Element-Vollbild — dort greift ein CSS-Fallback
        // (position: fixed) mit identischer Optik.
        if (fsBtn) {
            const fsRequest = container.requestFullscreen || container.webkitRequestFullscreen;

            const isRealFs = () =>
                (document.fullscreenElement || document.webkitFullscreenElement) === container;
            const isFallbackFs = () => container.classList.contains('chat-vp--fs-fallback');
            const isFsActive   = () => isRealFs() || isFallbackFs();

            const syncFsButton = (active) => {
                if (fsExpandIcon) fsExpandIcon.style.display = active ? 'none' : '';
                if (fsCompIcon)   fsCompIcon.style.display   = active ? '' : 'none';
                const label = active ? t('videoExitFullscreenAria', 'Vollbild beenden')
                                     : t('videoFullscreenAria',     'Vollbild');
                fsBtn.setAttribute('aria-label', label);
                fsBtn.setAttribute('title', label);
                container.classList.toggle('chat-vp--fs', !!active);
            };

            // Orientierung an das Videoformat koppeln: Querformat-Video dreht das
            // Geraet mit, Hochformat bleibt hochkant. lock() gibt es nur im echten
            // Vollbild und nur auf Mobil-Browsern — Rejects sind normal.
            const lockOrientation = () => {
                const orientation = window.screen?.orientation;
                if (!orientation || typeof orientation.lock !== 'function') return;
                const vw = video.videoWidth;
                const vh = video.videoHeight;
                if (!vw || !vh) {
                    // preload="none": Metadaten kommen erst spaeter. Dann nachziehen,
                    // falls der Nutzer noch im Vollbild ist.
                    video.addEventListener('loadedmetadata', () => {
                        if (isFsActive()) lockOrientation();
                    }, { once: true });
                    return;
                }
                let target = null;
                if      (vw > vh * 1.1) target = 'landscape';
                else if (vh > vw * 1.1) target = 'portrait';
                if (!target) return;
                try {
                    const p = orientation.lock(target);
                    if (p && typeof p.catch === 'function') {
                        p.catch(() => this._vpLog('info', 'orientation lock rejected (' + target + ')'));
                    }
                } catch (_) { /* Desktop / nicht unterstuetzt */ }
            };
            const unlockOrientation = () => {
                try { window.screen?.orientation?.unlock?.(); } catch (_) { /* noop */ }
            };

            const onFsChange = () => {
                if (isRealFs()) {
                    syncFsButton(true);
                    lockOrientation();
                } else {
                    document.removeEventListener('fullscreenchange',       onFsChange);
                    document.removeEventListener('webkitfullscreenchange', onFsChange);
                    syncFsButton(false);
                    unlockOrientation();
                }
            };

            // Fallback-Vollbild: eigener Escape-Handler in der Capture-Phase, damit
            // die Galerie-Lightbox (eigener keydown auf document) nicht gleichzeitig
            // zugeht. MutationObserver raeumt auf, falls der Player waehrend des
            // Fallbacks aus dem DOM fliegt (Re-Render, Lightbox-Close).
            let fsKeyHandler   = null;
            let fsDetachWatch  = null;

            const exitFallbackFs = () => {
                container.classList.remove('chat-vp--fs-fallback');
                document.body.classList.remove('chat-vp-fs-lock');
                if (fsKeyHandler) {
                    document.removeEventListener('keydown', fsKeyHandler, true);
                    fsKeyHandler = null;
                }
                if (fsDetachWatch) { fsDetachWatch.disconnect(); fsDetachWatch = null; }
                syncFsButton(false);
                unlockOrientation();
            };

            const enterFallbackFs = () => {
                container.classList.add('chat-vp--fs-fallback');
                document.body.classList.add('chat-vp-fs-lock');
                fsKeyHandler = (e) => {
                    if (e.key !== 'Escape') return;
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    exitFallbackFs();
                };
                document.addEventListener('keydown', fsKeyHandler, true);
                fsDetachWatch = new MutationObserver(() => {
                    if (!container.isConnected) exitFallbackFs();
                });
                fsDetachWatch.observe(document.body, { childList: true, subtree: true });
                syncFsButton(true);
                lockOrientation();
                this._vpLog('info', 'fullscreen fallback entered');
            };

            // Handle fuer exitFullscreenFallback() — die Zurueck-Navigation
            // (navigation.js) muss den Pseudo-Vollbild-Modus beenden koennen,
            // ohne diese Closure zu kennen.
            container._vpExitFallbackFs = exitFallbackFs;

            const enterFullscreen = () => {
                if (!fsRequest) { enterFallbackFs(); return; }
                document.addEventListener('fullscreenchange',       onFsChange);
                document.addEventListener('webkitfullscreenchange', onFsChange);
                try {
                    const p = fsRequest.call(container);
                    if (p && typeof p.catch === 'function') {
                        p.catch(() => {
                            document.removeEventListener('fullscreenchange',       onFsChange);
                            document.removeEventListener('webkitfullscreenchange', onFsChange);
                            this._vpLog('warn', 'requestFullscreen rejected, using fallback');
                            enterFallbackFs();
                        });
                    }
                } catch (_) {
                    document.removeEventListener('fullscreenchange',       onFsChange);
                    document.removeEventListener('webkitfullscreenchange', onFsChange);
                    enterFallbackFs();
                }
            };

            const exitFullscreen = () => {
                if (isFallbackFs()) { exitFallbackFs(); return; }
                const exit = document.exitFullscreen || document.webkitExitFullscreen;
                if (exit) {
                    try {
                        const p = exit.call(document);
                        if (p && typeof p.catch === 'function') p.catch(() => {});
                    } catch (_) { /* noop */ }
                }
            };

            fsBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                cancelHide();
                container.classList.remove('chat-vp-controls--hidden');
                if (isFsActive()) exitFullscreen();
                else              enterFullscreen();
                scheduleHide(2500);
            });
        }

        if (messageId && ccBtn) {
            // Reflect current captions state on the new button.
            const msgs0 = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
            const msg0  = msgs0.find(m => m.id === messageId);
            const initialOn = msg0 && msg0._captionsOn !== false;
            ccBtn.classList.toggle('chat-video-cc-btn--active', !!initialOn);
            ccBtn.setAttribute('aria-label',
                initialOn ? t('videoCaptionsOffAria', 'Untertitel ausblenden')
                          : t('videoCaptionsOnAria',  'Untertitel einblenden')
            );

            ccBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleVideoCaptions(messageId);
                const m = (VCS.state.chatMessages[VCS.state.chatActiveChatId] || []).find(x => x.id === messageId);
                const on = m && m._captionsOn !== false;
                ccBtn.classList.toggle('chat-video-cc-btn--active', !!on);
                ccBtn.setAttribute('aria-label',
                    on ? t('videoCaptionsOffAria', 'Untertitel ausblenden')
                       : t('videoCaptionsOnAria',  'Untertitel einblenden')
                );
            });
        }

        // ── Scrubber drag/tap ───────────────────────────────
        const seekFromEvent = (clientX) => {
            const rect = scrubber.getBoundingClientRect();
            const pct  = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
            const dur  = video.duration;
            if (isFinite(dur) && dur > 0) {
                video.currentTime = pct * dur;
            }
        };
        let scrubbing = false;
        const onMove = (e) => {
            if (!scrubbing) return;
            const x = e.touches ? e.touches[0].clientX : e.clientX;
            seekFromEvent(x);
            cancelHide();
            e.preventDefault();
        };
        const onUp = () => {
            if (!scrubbing) return;
            scrubbing = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup',   onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend',  onUp);
            scheduleHide(2500);
        };
        const onDown = (e) => {
            // m4: ignore taps while video is buffering (readyState < 2).
            if (video.readyState < 2) return;
            scrubbing = true;
            const x = e.touches ? e.touches[0].clientX : e.clientX;
            seekFromEvent(x);
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup',   onUp);
            window.addEventListener('touchmove', onMove, {passive: false});
            window.addEventListener('touchend',  onUp);
            e.stopPropagation();
        };
        scrubber.addEventListener('mousedown',  onDown);
        scrubber.addEventListener('touchstart', onDown, {passive: true});

        // ── Tap on video toggles play/pause (no bar toggle — Tom-Triage M1) ──
        video.addEventListener('click', () => {
            if (video.paused) safePlay();
            else              video.pause();
        });

        // ── Pointer activity reveals bar (mouse-only; touch handled via taps) ──
        container.addEventListener('mousemove', () => { if (!video.paused) showBar(); });

        // ── Initial overlay-play wiring (overlayPlay was inserted in _build…) ──
        if (overlayPlay) {
            overlayPlay.addEventListener('click', (e) => {
                e.stopPropagation();
                safePlay();
            });
        }
    }

    onVideoWords(msg) {
        const msgs = VCS.state.chatMessages[msg.chatId] || [];
        const m = msgs.find(x => x.id === msg.messageId);
        if (m) {
            m._videoWords = msg.original || [];
            if (msg.translated) {
                m._videoWordsTranslated = msg.translated;
                m._videoTargetLang = msg.targetLang;
            }
            // Karaoke live an laufendes Video anhaengen (ohne Re-Render)
            const playingVideo = document.querySelector(`video[data-msg-id="${msg.messageId}"]`);
            if (playingVideo) {
                const captionEl = playingVideo.parentElement?.querySelector('.chat-video-caption');
                if (captionEl) {
                    const karaokeUpdate = this.videoKaraoke(playingVideo, msg.messageId, captionEl);
                    if (karaokeUpdate) playingVideo.ontimeupdate = karaokeUpdate;
                }
            } else if (VCS.state.chatActiveChatId === msg.chatId) {
                // Nur re-rendern wenn kein Video laeuft
                VCS.chatMessages?.renderChatMessages?.();
            }
        }
    }

    videoKaraoke(video, messageId, captionEl, forceOriginal) {
        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msg = msgs.find(m => m.id === messageId);
        const useTranslated = !forceOriginal
            && msg?._videoWordsTranslated && msg._videoWordsTranslated.length;
        const words = useTranslated ? msg._videoWordsTranslated : msg?._videoWords;
        if (!captionEl || !words || !words.length) return null;

        // Respect user's CC toggle (YouTube-style).
        // Cross-language: always ON. Same-language: user pref from localStorage, default OFF.
        if (msg && typeof msg._captionsOn !== 'boolean') {
            const myLang = VCS.state.currentLang || 'de';
            const sameLang = msg.senderLang === myLang;
            if (sameLang) {
                msg._captionsOn = localStorage.getItem('vcs_cc_pref_samelang') === '1';
            } else {
                msg._captionsOn = true;
            }
        }

        const segments = [];
        let cur = null;
        for (let i = 0; i < words.length; i++) {
            const w = words[i];
            const token = w.word || '';
            if (!token) continue;
            if (!cur) { cur = {start: w.start, end: w.end, words: [w]}; continue; }
            cur.end = w.end;
            cur.words.push(w);
            // Segment abschliessen bei Satzzeichen oder Sprechpause (> 0.5s)
            const next = words[i + 1];
            const gap = next ? next.start - w.end : 0;
            if (/[.!?]$/.test(token) || gap > 0.3) {
                segments.push(cur);
                cur = null;
            }
        }
        if (cur) segments.push(cur);

        let lastSegIdx = -1;
        let lastWordIdx = -1;

        const update = () => {
            // CC toggle off → ensure caption is cleared and skip updates.
            if (msg && msg._captionsOn === false) {
                if (lastSegIdx !== -1) { captionEl.textContent = ''; lastSegIdx = -1; lastWordIdx = -1; }
                return;
            }
            const ct = video.currentTime;
            let segIdx = -1;
            for (let i = 0; i < segments.length; i++) {
                if (ct >= segments[i].start - 0.1 && ct <= segments[i].end + 0.3) { segIdx = i; break; }
            }
            if (segIdx === -1) {
                for (let i = segments.length - 1; i >= 0; i--) {
                    if (ct >= segments[i].end && ct <= segments[i].end + 1.0) { segIdx = i; break; }
                }
            }

            if (segIdx === -1) {
                if (lastSegIdx !== -1) { captionEl.textContent = ''; lastSegIdx = -1; lastWordIdx = -1; }
                return;
            }

            const seg = segments[segIdx];
            if (segIdx !== lastSegIdx) {
                captionEl.innerHTML = '';
                seg.words.forEach((w, i) => {
                    const span = document.createElement('span');
                    span.textContent = (i > 0 ? ' ' : '') + w.word;
                    span.className = 'chat-vp-word';
                    captionEl.appendChild(span);
                });
                lastSegIdx = segIdx;
                lastWordIdx = -1;
            }

            const spans = captionEl.querySelectorAll('.chat-vp-word');
            let activeIdx = -1;
            for (let i = seg.words.length - 1; i >= 0; i--) {
                if (ct >= seg.words[i].start) { activeIdx = i; break; }
            }
            if (activeIdx !== lastWordIdx) {
                spans.forEach((s, i) => {
                    s.classList.toggle('chat-vp-word--active', i <= activeIdx);
                });
                lastWordIdx = activeIdx;
            }
        };

        return update;
    }

    toggleVideoCaptions(messageId) {
        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msg = msgs.find(m => m.id === messageId);
        if (!msg) return;

        msg._captionsOn = !msg._captionsOn;

        // Persist user pref only for same-language videos. Cross-language
        // toggles stay session-only — next page load restores ON.
        const myLang = VCS.state.currentLang || 'de';
        const sameLang = msg.senderLang === myLang;
        if (sameLang) {
            try { localStorage.setItem('vcs_cc_pref_samelang', msg._captionsOn ? '1' : '0'); } catch (_) {}
        }

        // Reflect button active-state in the DOM without re-rendering.
        const bubble = document.querySelector(`.chat-bubble[data-msg-id="${messageId}"]`);
        const btn = bubble?.querySelector('.chat-video-cc-btn');
        if (btn) btn.classList.toggle('chat-video-cc-btn--active', msg._captionsOn);

        // If a video is currently playing, the existing ontimeupdate handler
        // inspects msg._captionsOn on every tick — clearing captions on toggle-off
        // and re-drawing on toggle-on. No extra wiring needed here.
        const captionEl = bubble?.querySelector('.chat-video-caption');
        if (captionEl && !msg._captionsOn) {
            captionEl.textContent = '';
        }
    }

    async setVideoVariant(messageId, showTranslated, scopeEl) {
        const msgs = VCS.state.chatMessages[VCS.state.chatActiveChatId] || [];
        const msg = msgs.find(m => m.id === messageId);
        if (!msg) return;

        const hasVideoWords = !!(msg._videoWords && msg._videoWords.length);
        msg._preferTranslatedVideo = !!showTranslated;

        // scopeEl erlaubt es, den Player-Container aus einem anderen Kontext
        // (z.B. Galerie-Lightbox) zu uebergeben. Default: Chat-Bubble per Selector.
        const bubble = scopeEl || document.querySelector(`.chat-bubble[data-msg-id="${messageId}"]`);
        if (!bubble) return;

        if (hasVideoWords) {
            // Neue Videos: Karaoke mit Original- oder uebersetzten Woertern
            const video = bubble.querySelector('video');
            const captionEl = bubble.querySelector('.chat-video-caption');
            if (video && captionEl) {
                captionEl.textContent = '';
                const karaokeUpdate = this.videoKaraoke(video, messageId, captionEl, !showTranslated);
                if (karaokeUpdate) video.ontimeupdate = karaokeUpdate;
            }
        } else {
            // Alte Videos: Video-Src wechseln (Original ↔ Captioned)
            if (!msg.translatedVideoUrl) return;
            const translatedSrc = showTranslated
                ? (await VCS.e2e?.getDecryptedTranslatedVideoUrl?.(msg)) || msg.translatedVideoUrl
                : null;
            const vidSrc = showTranslated
                ? translatedSrc
                : (msg._decryptedMediaUrl || msg.mediaUrl);
            const video = bubble.querySelector('video');
            const poster = bubble.querySelector('.chat-video-poster');
            if (video) {
                video.style.opacity = '0';
                setTimeout(() => {
                    video.src = vidSrc;
                    video.style.opacity = '1';
                }, 200);
            } else if (poster) {
                poster.setAttribute('onclick', `VCS.chatVideo?.playVideo?.(this, '${vidSrc.replace(/'/g, "\\'")}', null, true, null, null, false)`);
            }
        }

        VCS.ui?.showToast?.(
            showTranslated ? t('videoTranslatedActive') : t('videoOriginalActive'),
            'info', 1600
        );

        const btns = bubble.querySelectorAll('.chat-video-toggle-btn');
        if (btns.length === 2) {
            btns[0].classList.toggle('chat-video-toggle-btn--active', !showTranslated);
            btns[1].classList.toggle('chat-video-toggle-btn--active', !!showTranslated);
        }
    }

    isDeferredVideoMessage(msg) {
        return !!msg
            && msg.mediaType === 'video'
            && msg.translatedVideoStatus === 'processing'
            && !msg.translatedVideoUrl;
    }

    ensureVideoProcessingPoll(chatId) {
        if (!chatId || VCS.state.chatVideoProcessingPollTimers[chatId]) {
            return;
        }
        VCS.state.chatVideoProcessingPollTimers[chatId] = setInterval(async () => {
            const localMsgs = VCS.state.chatMessages[chatId] || [];
            const hasPending = localMsgs.some(m => this.isDeferredVideoMessage(m));
            if (!hasPending) {
                clearInterval(VCS.state.chatVideoProcessingPollTimers[chatId]);
                delete VCS.state.chatVideoProcessingPollTimers[chatId];
                return;
            }

            try {
                const res = await fetch(VCS.Chat.API_BASE + '/messages/' + encodeURIComponent(chatId), {
                    headers: VCS.chat?.authHeaders()
                });
                if (!res.ok) {
                    return;
                }
                const serverMsgs = await res.json();
                let changed = false;

                for (const srv of serverMsgs) {
                    if (!srv || srv.mediaType !== 'video') {
                        continue;
                    }
                    const local = localMsgs.find(m => m.id === srv.id);
                    if (!local) {
                        continue;
                    }

                    if (srv.translatedVideoUrl && local.translatedVideoUrl !== srv.translatedVideoUrl) {
                        local.translatedVideoUrl = srv.translatedVideoUrl;
                        changed = true;
                    }
                    if (srv.translatedVideoLang && local.translatedVideoLang !== srv.translatedVideoLang) {
                        local.translatedVideoLang = srv.translatedVideoLang;
                        changed = true;
                    }
                    if (srv.translatedVideoStatus && local.translatedVideoStatus !== srv.translatedVideoStatus) {
                        local.translatedVideoStatus = srv.translatedVideoStatus;
                        changed = true;
                    }
                    if (local._deferredVideoPending && local.translatedVideoStatus !== 'processing') {
                        local._deferredVideoPending = false;
                        changed = true;
                    }
                }

                if (changed && VCS.state.chatActiveChatId === chatId) {
                    VCS.chatMessages?.renderChatMessages?.();
                    VCS.chatMessages?.scrollToBottom?.();
                }
            } catch (_) {
            }
        }, 3000);
    }

    async saveVideoMessage(messageId) {
        const msgs = (VCS.state.chatMessages[VCS.state.chatActiveChatId] || []).filter(m => !this.isDeferredVideoMessage(m));
        const msg = msgs.find(m => m.id === messageId);
        if (!msg) {
            return;
        }

        // Beim Speichern immer die captioned Version bevorzugen (wenn vorhanden)
        const hasCaptioned = !!msg.translatedVideoUrl;
        const sourceUrl = hasCaptioned
            ? msg.translatedVideoUrl
            : (msg._decryptedMediaUrl || msg._localBlobUrl || msg.mediaUrl);
        const preferTranslated = hasCaptioned;
        if (!sourceUrl || sourceUrl === 'pending') {
            return;
        }

        const fallbackName = 'video-' + (messageId || Date.now()) + '.mp4';
        const baseName = (msg.mediaFileName && msg.mediaFileName.trim()) ? msg.mediaFileName : fallbackName;
        const filename = preferTranslated
            ? baseName.replace(/(\.[^.]+)?$/, '_translated$1')
            : baseName;

        try {
            let downloadUrl = sourceUrl;
            let revokeAfter = false;

            if (!sourceUrl.startsWith('blob:')) {
                const resp = await fetch(sourceUrl);
                if (!resp.ok) {
                    throw new Error('download failed');
                }
                const blob = await resp.blob();
                downloadUrl = URL.createObjectURL(blob);
                revokeAfter = true;
            }

            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();

            if (revokeAfter) {
                setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
            }
        } catch (e) {
            console.error('[chat] saveVideo error:', e);
            VCS.ui?.showToast?.(t('mediaDownloadFailed'), 'error');
        }
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        // leer
    }
};
